// Central Dataverse environment resolution.
//
// getEnvironmentUrl() returns the per-tenant Dataverse URL resolved at runtime
// (via the Global Discovery Service in the Setup Wizard, then carried back on
// every /redeem response) when one has been stored for this tenant. When none
// exists — which includes Scott's own tenant, and any tenant that has never run
// the discovery flow — it falls back to the EXACT existing build-time
// REACT_APP_DATAVERSE_URL behavior, unchanged.
//
// This is the single source of truth for the Dataverse API base AND the
// Dataverse token scope, so a resolved environment is talked to consistently.

const RESOLVED_ENV_KEY = 'dagrun_pm_dataverse_env';

/** The resolved per-tenant Dataverse URL, or '' if none is stored. */
function readResolved(): string {
  try {
    const v = localStorage.getItem(RESOLVED_ENV_KEY);
    return v && v.trim() ? v.trim() : '';
  } catch {
    return '';
  }
}

/**
 * The Dataverse environment URL to use. Runtime-resolved value when present;
 * otherwise the unchanged build-time REACT_APP_DATAVERSE_URL fallback.
 */
export function getEnvironmentUrl(): string {
  return readResolved() || process.env.REACT_APP_DATAVERSE_URL || '';
}

/** Persist a runtime-resolved Dataverse URL. No-op for empty values. */
export function setResolvedEnvironmentUrl(url: string | null | undefined): void {
  if (!url || !url.trim()) return;
  try {
    localStorage.setItem(RESOLVED_ENV_KEY, url.trim());
  } catch {
    /* storage unavailable — stay on the fallback */
  }
}

/** Whether a runtime-resolved environment is in effect (vs. the build-time fallback). */
export function hasResolvedEnvironment(): boolean {
  return readResolved().length > 0;
}

export function clearResolvedEnvironmentUrl(): void {
  try {
    localStorage.removeItem(RESOLVED_ENV_KEY);
  } catch {
    /* ignore */
  }
}

/** Dataverse Web API base for the resolved (or fallback) environment. */
export function getDataverseApiBase(): string {
  return `${getEnvironmentUrl()}/api/data/v9.2`;
}

/** Dataverse delegated token scope for the resolved (or fallback) environment. */
export function getDataverseScopes(): string[] {
  return [`${getEnvironmentUrl()}/user_impersonation`];
}
